/**
 * 2 · La evolución — lo que justifica el retainer.
 *
 * Sigue el Evolution Pattern de DESIGN_SYSTEM.md al pie: una fila por métrica,
 * la serie entera en el medio, el delta contra la línea de base. "Antes → ahora"
 * no distingue una mejora sostenida de un salto que se cayó, y esa distinción es
 * el servicio.
 *
 * Las cuatro métricas no salen del mismo lado, a propósito:
 *   · puntaje, hallazgos de SEO y críticos de accesibilidad vienen de la serie
 *     de mediciones, que crece con cada publicación;
 *   · los clics vienen de Search Console y solo tienen DOS puntos —el período y
 *     el anterior—, que es el mínimo del pattern. Con eso alcanza para decir si
 *     subió, y es todo lo que la API consolidada permite decir sin inventar.
 */
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../../ui/DetailSection.js';
import { EvolutionRow } from '../../ui/EvolutionRow.js';
import type { Medicion as MedicionDeLaFila } from '../../ui/EvolutionRow.js';
import type { EstadoMantenimiento } from '../../../hooks/useMantenimiento.js';
import { usePeriodo } from '../../../hooks/useMantenimiento.js';

interface Props {
  projectId: number;
  estado: EstadoMantenimiento;
}

export function SeccionEvolucion({ projectId, estado }: Props) {
  const { t } = useTranslation();
  const { data: periodo } = usePeriodo(projectId, true);

  const serie = estado.serie;

  /** Una fila de la serie, saltando las mediciones que no tienen ese dato. */
  const de = (valor: (m: EstadoMantenimiento['serie'][number]) => number | null): MedicionDeLaFila[] =>
    serie
      .map(m => ({ fecha: m.auditedAt, valor: valor(m) }))
      .filter((p): p is MedicionDeLaFila => p.valor !== null);

  const puntaje = de(m => m.score);
  const hallazgosSeo = de(m => (m.seo ? m.seo.length : null));
  const criticos = de(m => m.accesibilidad.critical);

  // Los clics: dos puntos, o ninguno. `busquedas.hay === false` trae el motivo
  // por el que no se midió, y ese motivo NO es un cero — dibujar cero clics
  // cuando lo que pasa es que no hay Search Console conectada sería decirle al
  // cliente que nadie lo busca.
  const b = periodo?.datos.busquedas;
  const clicks: MedicionDeLaFila[] = periodo && b?.hay && b.dato.anterior
    ? [
      { fecha: periodo.datos.ventana.desde, valor: b.dato.anterior.clicks },
      { fecha: periodo.datos.ventana.hasta, valor: b.dato.periodo.clicks },
    ]
    : [];

  const hayAlgo = puntaje.length >= 2 || hallazgosSeo.length >= 2 || criticos.length >= 2 || clicks.length >= 2;

  return (
    <DetailSection title={t('projects.maintenance.evolution.title')}>
      {hayAlgo
        ? (
          <>
            <EvolutionRow label={t('projects.maintenance.evolution.score')} mediciones={puntaje} mejorEsMas />
            <EvolutionRow label={t('projects.maintenance.evolution.seo')} mediciones={hallazgosSeo} mejorEsMas={false} />
            <EvolutionRow label={t('projects.maintenance.evolution.critical')} mediciones={criticos} mejorEsMas={false} />
            <EvolutionRow label={t('projects.maintenance.evolution.clicks')} mediciones={clicks} mejorEsMas last />
            {estado.comparacion
              ? (
                <p style={{
                  margin: 'var(--evo-space-3) 0 0',
                  fontSize: 'var(--evo-fs-tiny)',
                  color: 'var(--evo-fg-4)',
                  fontFamily: 'var(--evo-font)',
                }}>
                  {t('projects.maintenance.evolution.publications', { count: estado.comparacion.publicaciones })}
                </p>
              )
              : null}
          </>
        )
        : (
          <p style={{
            margin: 0,
            fontSize: 'var(--evo-fs-body)',
            color: 'var(--evo-text-muted)',
            fontFamily: 'var(--evo-font)',
          }}>
            {t('projects.maintenance.evolution.empty')}
          </p>
        )}
    </DetailSection>
  );
}
